import { onFind } from "@elements/init-modules-in-scope";
import { findIn, closest, on, off, trigger, findAllIn, setAttribute } from "@elements/dom-utils";
// @ts-ignore
import { getPrefixedDataSet } from '@elements/data-set-utils';
// Form Validation imports
import { formValidation } from '@form-validation/core';
// Plugins
import { Message } from '@form-validation/plugin-message';
import { Bootstrap5 } from '@form-validation/plugin-bootstrap5';
import { SubmitButton } from '@form-validation/plugin-submit-button';
import { Trigger } from '@form-validation/plugin-trigger';
import { Excluded } from '@form-validation/plugin-excluded';
import { Declarative } from '@form-validation/plugin-declarative';
import { AutoFocus } from '@form-validation/plugin-auto-focus';
import { Aria } from '@form-validation/plugin-aria';
// Validators
import { notEmpty } from '@form-validation/validator-not-empty';
import { emailAddress } from '@form-validation/validator-email-address';
import { file } from '@form-validation/validator-file';
import { integer } from '@form-validation/validator-integer';
import { regexp } from '@form-validation/validator-regexp';
import { lessThan } from '@form-validation/validator-less-than';
import { greaterThan } from '@form-validation/validator-greater-than';
const INITIALIZED_EVENT = 'form-validation.initialized';
const SUBMIT_EVENT = 'form-validation.submit';
const defaultOptions = {
    defaultSubmit: true,
    disableButtonOnSubmit: false,
    trigger: { event: 'change' },
};
const defaultSelectors = {
    base: '.js-form-validation',
    group: '.js-form-validation__group',
    errors: '.js-form-validation__errors'
};
export function init(options = defaultOptions, selectors = defaultSelectors, scope = document.body) {
    onFind(selectors.base, function (element) {
        Promise.all([loadLocales()]).then(([lang]) => {
            createFormValidation(element, { ...defaultOptions, ...options }, { ...defaultSelectors, ...selectors }, lang);
        });
    }, scope);
}
export async function createFormValidation(baseElement, options = defaultOptions, selectors = defaultSelectors, lang) {
    let api = { getFormInstance };
    let elementOptions = {
        ...defaultOptions,
        ...options,
        ...getPrefixedDataSet('form-validation', baseElement)
    };
    let formObject;
    let locale = window._config.locale || 'en_US';
    formObject = formValidation(baseElement, {
        locale: locale,
        localization: lang[locale],
        plugins: {
            declarative: new Declarative({
                html5Input: true
            }),
            // @ts-ignore
            bootstrap5: new Bootstrap5({
                rowSelector: '.js-form-validation__group',
                rowInvalidClass: 'is-invalid',
                rowValidClass: 'is-valid',
                defaultMessageContainer: false,
            }),
            message: new Message({
                container: function (field, element) {
                    return findIn(selectors.errors, closest(selectors.group, element));
                }
            }),
            submitButton: new SubmitButton(),
            autoFocus: new AutoFocus(),
            aria: new Aria(),
            trigger: new Trigger(elementOptions.trigger),
            excluded: new Excluded()
        }
    })
        // Set the default validators
        .registerValidator('notEmpty', notEmpty)
        .registerValidator('emailAddress', emailAddress)
        .registerValidator('file', file)
        .registerValidator('integer', integer)
        .registerValidator('regexp', regexp)
        .registerValidator('lessThan', lessThan)
        .registerValidator('greaterThan', greaterThan)
        // handle valid form
        .on('core.form.valid', () => {
        if (baseElement.getAttribute("data-action")) {
            changeAction(baseElement);
        }
        if (elementOptions.defaultSubmit === true) {
            if (elementOptions.disableButtonOnSubmit) {
                let btnToDisable = findAllIn('button[type="submit"], button:not([type])', baseElement);
                btnToDisable.map((btn) => {
                    setAttribute('disabled', 'disabled', btn);
                });
            }
            baseElement.submit();
        }
        else {
            trigger(SUBMIT_EVENT, baseElement);
        }
    });
    function getFormInstance() {
        return formObject;
    }
    //@ts-ignore
    baseElement.formValidation = api;
    trigger(INITIALIZED_EVENT, baseElement);
    return api;
}
const changeAction = (element) => {
    let newAction = element.getAttribute("data-action");
    if (newAction) {
        setAttribute('action', newAction, element);
    }
};
let promise = null;
export function loadLocales() {
    if (promise) {
        return promise;
    }
    let lang = window._config.locale || 'en_US';
    promise = new Promise((resolve, reject) => {
        let data;
        switch (lang) {
            case 'ar_MA':
                data = import('@form-validation/locales/ar_MA');
                break;
            case 'bg_BG':
                data = import(`@form-validation/locales/bg_BG`);
                break;
            case 'ca_ES':
                data = import(`@form-validation/locales/ca_ES`);
                break;
            case 'cs_CZ':
                data = import(`@form-validation/locales/cs_CZ`);
                break;
            case 'da_DK':
                data = import(`@form-validation/locales/da_DK`);
                break;
            case 'de_DE':
                data = import(`@form-validation/locales/de_DE`);
                break;
            case 'de_AT':
                data = import(`@form-validation/locales/de_DE`);
                break;
            case 'el_GR':
                data = import(`@form-validation/locales/el_GR`);
                break;
            case 'en_US':
                data = import(`@form-validation/locales/en_US`);
                break;
            case 'es_CL':
                data = import(`@form-validation/locales/es_CL`);
                break;
            case 'es_ES':
                data = import(`@form-validation/locales/es_ES`);
                break;
            case 'eu_ES':
                data = import(`@form-validation/locales/eu_ES`);
                break;
            case 'fa_IR':
                data = import(`@form-validation/locales/fa_IR`);
                break;
            case 'fi_FI':
                data = import(`@form-validation/locales/fi_FI`);
                break;
            case 'fr_BE':
                data = import(`@form-validation/locales/fr_BE`);
                break;
            case 'fr_FR':
                data = import(`@form-validation/locales/fr_FR`);
                break;
            case 'he_IL':
                data = import(`@form-validation/locales/he_IL`);
                break;
            case 'hi_IN':
                data = import(`@form-validation/locales/hi_IN`);
                break;
            case 'hu_HU':
                data = import(`@form-validation/locales/hu_HU`);
                break;
            case 'id_ID':
                data = import(`@form-validation/locales/id_ID`);
                break;
            case 'it_IT':
                data = import(`@form-validation/locales/it_IT`);
                break;
            case 'ja_JP':
                data = import(`@form-validation/locales/ja_JP`);
                break;
            case 'nl_BE':
                data = import(`@form-validation/locales/nl_BE`);
                break;
            case 'nl_NL':
                data = import(`@form-validation/locales/nl_NL`);
                break;
            case 'no_NO':
                data = import(`@form-validation/locales/no_NO`);
                break;
            case 'pl_PL':
                data = import(`@form-validation/locales/pl_PL`);
                break;
            case 'pt_BR':
                data = import(`@form-validation/locales/pt_BR`);
                break;
            case 'pt_PT':
                data = import(`@form-validation/locales/pt_PT`);
                break;
            case 'ro_RO':
                data = import(`@form-validation/locales/ro_RO`);
                break;
            case 'ru_RU':
                data = import(`@form-validation/locales/ru_RU`);
                break;
            case 'sk_SK':
                data = import(`@form-validation/locales/sk_SK`);
                break;
            case 'sq_AL':
                data = import(`@form-validation/locales/sq_AL`);
                break;
            case 'sr_RS':
                data = import(`@form-validation/locales/sr_RS`);
                break;
            case 'sv_SE':
                data = import(`@form-validation/locales/sv_SE`);
                break;
            case 'th_TH':
                data = import(`@form-validation/locales/th_TH`);
                break;
            case 'tr_TR':
                data = import(`@form-validation/locales/tr_TR`);
                break;
            case 'ua_UA':
                data = import(`@form-validation/locales/ua_UA`);
                break;
            case 'vi_VN':
                data = import(`@form-validation/locales/vi_VN`);
                break;
            case 'zh_CN':
                data = import(`@form-validation/locales/zh_CN`);
                break;
            case 'zh_TW':
                data = import(`@form-validation/locales/zh_TW`);
                break;
            default:
                data = import(`@form-validation/locales/en_US`);
                break;
        }
        resolve(data);
    });
    return promise;
}
export function getApi(element) {
    if (element.formValidation) {
        return Promise.resolve(element.formValidation);
    }
    else {
        return new Promise(function (resolve, reject) {
            function initializeHandler() {
                resolve(element.formValidation);
                off(INITIALIZED_EVENT, initializeHandler, element);
            }
            on(INITIALIZED_EVENT, initializeHandler, element);
        });
    }
}
